
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WeekSelector } from './WeekSelector';
import { startOfWeek, format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/UserContext';

interface TeamVisibilitySectionProps {
  currentWeek: Date;
  onWeekChange: (date: Date) => void;
}

type Team = {
  id: string;
  name: string;
};

export const TeamVisibilitySection: React.FC<TeamVisibilitySectionProps> = ({
  currentWeek,
  onWeekChange
}) => {
  const { t } = useLanguage();
  const { user, isAdmin, isManager, adminTeamIds } = useUser();
  const queryClient = useQueryClient();
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const formattedDate = format(weekStart, 'yyyy-MM-dd');
  
  // Fetch all teams that the user can manage
  const { data: teams = [], isLoading: isLoadingTeams } = useQuery({
    queryKey: ['manageable-teams', isAdmin, isManager, user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      if (!isAdmin && !isManager) return [];
      
      let query = supabase.from('teams').select('id, name').order('name');
      
      // If not an admin, filter to only teams the manager can access
      if (!isAdmin && isManager) {
        const { data: managerTeams } = await supabase
          .from('manager_teams')
          .select('team_id')
          .eq('manager_id', user.id);
        
        if (managerTeams && managerTeams.length > 0) {
          const teamIds = managerTeams.map(mt => mt.team_id);
          query = query.in('id', teamIds);
        } else {
          return [];
        }
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      return (data as Team[]) || [];
    },
    enabled: !!user && (isAdmin || isManager),
    staleTime: 300000, // 5 minutes
  });

  // Fetch visibility status for all teams in current week
  const { data: visibilityData = [] } = useQuery({
    queryKey: ['week-visibility', formattedDate, teams?.map(t => t.id)],
    queryFn: async () => {
      if (!teams || teams.length === 0) return [];
      
      const { data, error } = await supabase
        .from('week_visibility')
        .select('team_id, is_visible')
        .eq('week_start_date', formattedDate)
        .in('team_id', teams.map(team => team.id));
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!teams && teams.length > 0,
    staleTime: 30000, // 30 seconds
  });

  // Memoize visibility status map
  const visibilityStatus = useMemo(() => {
    const status: Record<string, boolean> = {};
    teams.forEach(team => {
      const teamVisibility = visibilityData.find(v => v.team_id === team.id);
      status[team.id] = teamVisibility?.is_visible ?? false;
    });
    return status;
  }, [teams, visibilityData]);
  
  // Mutation for updating visibility with optimistic updates
  const visibilityMutation = useMutation({
    mutationFn: async ({ teamId, isVisible }: { teamId: string; isVisible: boolean }) => {
      const { error } = await supabase
        .from('week_visibility')
        .upsert({
          team_id: teamId,
          week_start_date: formattedDate,
          is_visible: isVisible
        }, {
          onConflict: 'team_id,week_start_date'
        });
      
      if (error) throw error;
      return { teamId, isVisible };
    },
    onMutate: async ({ teamId, isVisible }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['week-visibility', formattedDate] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData(['week-visibility', formattedDate, teams?.map(t => t.id)]);
      
      // Optimistically update
      queryClient.setQueryData(['week-visibility', formattedDate, teams?.map(t => t.id)], (old: any[] = []) => {
        const updated = old.filter(v => v.team_id !== teamId);
        return [...updated, { team_id: teamId, is_visible: isVisible }];
      });
      
      return { previousData };
    },
    onError: (error, variables, context) => {
      // Revert on error
      if (context?.previousData) {
        queryClient.setQueryData(['week-visibility', formattedDate, teams?.map(t => t.id)], context.previousData);
      }
      toast.error(t('errorUpdatingVisibility') || 'Error updating week visibility');
    },
    onSuccess: (data) => {
      const statusMessage = data.isVisible
        ? t("weekVisibilityEnabled") || "Week schedule is now visible"
        : t("weekVisibilityDisabled") || "Week schedule is now hidden";
      toast.success(statusMessage);
    },
  });

  const handleVisibilityChange = (teamId: string, isVisible: boolean) => {
    visibilityMutation.mutate({ teamId, isVisible });
  };

  // Don't show the component if user has no permissions
  if (!isAdmin && !isManager) {
    return null;
  }

  return (
    <Card className="w-full max-w-full bg-gradient-to-r from-background to-muted/30 shadow-lg border-primary/20">
      <CardHeader className="pb-3 border-b border-primary/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="text-lg font-bold text-primary flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {t('teamVisibilityStatus') || 'Team Schedule Visibility'}
          </CardTitle>
          <WeekSelector 
            selectedWeek={weekStart}
            onSelectWeek={onWeekChange}
          />
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoadingTeams ? (
          <div className="flex justify-center items-center p-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-muted-foreground">Loading teams...</span>
            </div>
          </div>
        ) : teams && teams.length > 0 ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {teams.map((team) => (
              <div 
                key={team.id} 
                className="flex items-center justify-between border rounded-lg p-3 bg-background/50 hover:bg-background transition-all duration-200 shadow-sm hover:shadow-md min-w-0"
              >
                <span 
                  className="text-sm font-semibold flex-1 pr-3 text-foreground/90 leading-tight break-words"
                  title={team.name}
                  style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                >
                  {team.name}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className={`h-8 w-8 flex-shrink-0 transition-all duration-200 ${
                    visibilityStatus[team.id] 
                      ? 'border-green-500 hover:border-green-600 text-green-600 bg-green-50 hover:bg-green-100' 
                      : 'border-red-500 hover:border-red-600 text-red-600 bg-red-50 hover:bg-red-100'
                  }`}
                  onClick={() => handleVisibilityChange(team.id, !visibilityStatus[team.id])}
                  title={visibilityStatus[team.id] ? 'Hide team schedule' : 'Show team schedule'}
                >
                  {visibilityStatus[team.id] ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-6">
            <div className="text-muted-foreground text-lg font-medium">
              {isManager ? 
                (t('noTeamsAssigned') || 'No teams assigned to manage') :
                (t('noTeamsFound') || 'No teams found')
              }
            </div>
            <p className="text-sm text-muted-foreground/70 mt-2">
              {t('contactAdminForTeams') || 'Contact your administrator to assign teams'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
